import { Helmet } from 'react-helmet';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button, Space, Modal, Divider } from 'antd';
import { Link } from 'react-router-dom';

import { flatInfiniteData } from '@/utils/request/flatInfiniteData';

import {
  BankAccountFilter,
  BankAccountsFilterQuery,
} from '@/core/domain/enterprise/BankAccounts/BankAccounts.types';
import {
  useBankAccountsFilter,
  useDeleteBankAccount,
} from '@/core/domain/enterprise/BankAccounts/BankAccounts.hooks';

import { PageProps } from '@/types/PageProps';

import InfiniteTable, { ColumnsType } from '@/components/_common/InfiniteTable';
import DefaultRouteTabs from '@/components/_common/DefaultRouteTabs';
import {
  COLUMNS_WIDTH,
  getTableScrollX,
  tableSorterOrder,
} from '@/utils/table';
import EditIcon from '@/components/_common/EditIcon';
import DeleteIcon from '@/components/_common/DeleteIcon';
import { AppPageHeader } from '@/components/_common';
import BankAccountExtraFilters from '@/components/Registry/BankAccounts/BankAccountExtraFilters';
import { useDebounce } from '@/hooks';
import Authorities from '@/types/Authority';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';

function BankAccountsPage({ route }: PageProps) {
  const intl = useIntl();

  const [queryParams, setQueryParams] = useState<BankAccountsFilterQuery>({
    order: 'ASC',
    active: true,
    ...convertParams<BankAccountsFilterQuery>({
      search: 'string',
      description: 'string',
      bankId: 'number',
      bankBranch: 'string',
      number: 'string',
      active: 'boolean',
      order: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const bankAccountsFilter = useBankAccountsFilter(queryDebounce);

  const bankAccounts = flatInfiniteData(bankAccountsFilter.data);

  const removeMutation = useDeleteBankAccount();

  const handleRemoveClick = (planAccount: BankAccountFilter) => {
    Modal.confirm({
      title: intl.formatMessage({
        id: 'bankAccounts.remove',
      }),
      content: `${intl.formatMessage({
        id: 'bankAccounts.remove.modal.content',
      })}`,
      onOk: async () => {
        await removeMutation.mutateAsync(planAccount.id);
      },
      okType: 'danger',
    });
  };

  const columns: ColumnsType<BankAccountFilter> = [
    {
      dataIndex: 'bankName',
      key: 'bankName',
      title: intl.formatMessage({ id: 'bankAccounts.list.header.bank' }),
      width: 200,
      ellipsis: true,
      sorter: true,
      defaultSortOrder: queryParams.order === 'ASC' ? 'ascend' : 'descend',
    },
    {
      dataIndex: 'bankCode',
      title: intl.formatMessage({ id: 'bankAccounts.list.header.bankCode' }),
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: 'bankBranch',
      title: intl.formatMessage({ id: 'bankAccounts.list.header.agency' }),
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: 'bankNumber',
      title: intl.formatMessage({ id: 'bankAccounts.list.header.account' }),
      width: COLUMNS_WIDTH.default,
      ellipsis: true,
    },
    {
      dataIndex: 'description',
      title: intl.formatMessage({ id: 'bankAccounts.list.header.description' }),
      width: 200,
      ellipsis: true,
    },
    {
      title: intl.formatMessage({ id: 'bankAccounts.list.header.actions' }),
      render: (_, record) => (
        <Space>
          <EditIcon
            redirect={`/app/financial/accounts/bankAccounts/${record.id}`}
            disabled={!hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_VIEW])}
          />

          <Divider type="vertical" />

          <DeleteIcon
            onClick={() => handleRemoveClick(record)}
            disabled={
              !hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_DELETE])
            }
          />
        </Space>
      ),
      width: 100,
      align: 'right',
      fixed: 'right',
    },
  ];

  return (
    <DefaultRouteTabs
      permissions={route.permissions}
      persistSearchParams={false}
      route={route.parent}
      extra={
        <Link to="/app/financial/accounts/bankAccounts/create">
          <Button
            block
            type="primary"
            disabled={!hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_ADD])}
          >
            <FormattedMessage id="bankAccounts.searchBar.create" />
          </Button>
        </Link>
      }
    >
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <AppPageHeader
        onSearch={(value) =>
          setQueryParams((prev) => ({ ...prev, search: value }))
        }
        searchValue={queryParams.search}
        headerActions={({ isShowingFilters }) =>
          isShowingFilters && (
            <Button
              type="primary"
              onClick={() =>
                setQueryParams({
                  order: 'DESC',
                  active: true,
                })
              }
              disabled={
                !hasPermissions([Authorities.REGISTER_BANK_ACCOUNT_ADD])
              }
            >
              {intl.formatMessage({ id: 'commons.clear.filters' })}
            </Button>
          )
        }
        extraFilters={
          <BankAccountExtraFilters
            queryParams={queryParams}
            setQueryParams={setQueryParams}
          />
        }
      />

      <InfiniteTable<BankAccountFilter>
        columns={columns}
        dataSource={bankAccounts}
        loading={bankAccountsFilter.isFetching}
        rowKey={(row) => row.id}
        hasNextPage={bankAccountsFilter.hasNextPage}
        fetchNextPage={bankAccountsFilter.fetchNextPage}
        scroll={{ x: getTableScrollX(columns) }}
        onChange={(_p, _f, sorter) => {
          if (!Array.isArray(sorter)) {
            if (sorter && sorter.columnKey) {
              setQueryParams((prev) => {
                const order = sorter.order
                  ? tableSorterOrder[sorter.order]
                  : undefined;

                return { ...prev, order };
              });
            }
          }
        }}
      />
    </DefaultRouteTabs>
  );
}

export default BankAccountsPage;
